import {
	Edit,
	useUpdate,
	TextInput,
	SimpleForm,
	SelectInput,
	useNotify,
	useRedirect,
	useRefresh,
	useTranslate,
	useGetOne,
	AutocompleteInput,
	useGetList,
	TranslatableInputs,
} from "react-admin";
import { validateTaskData } from "./taskValidate";
import { CustomToolbar } from "../../customComponents/CustomToolbar";
import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { envSettings } from "../../settings/env";
import { taskEvents } from "../../utils/constants";
import { userPreferences } from "../../utils/fakeUserPrefrences";
import { useCustomGetListWithPagination } from "../../customHooks/customHooks";

export const TaskEdit = (props) => {
	const [modulesData, setModulesData] = useState([]);
	const [journalsData, setJournalsData] = useState([]);
	const [handoutsData, setHandoutsData] = useState([]);
	const [languagesArray, setLanguagesArray] = useState([]);
	const [events, setEvents] = useState([]);
	const [currentEvent, setCurrentEvent] = useState(null);
	const [loading, setLoading] = useState(true);
	const [handoutsLoading, setHandoutsLoading] = useState(true);
	const translate = useTranslate();
	const redirect = useRedirect();
	const refresh = useRefresh();
	const notify = useNotify();
	let { data: task, loading: taskLoading } = useGetOne("tasks", props.id);
	const onSuccess = () => {
		notify(translate("tasks.edit.success"), "info");
		redirect("/tasks");
		refresh();
	};

	let { loading: modulesLoading, fetchedData: modulesDataObj } =
		useCustomGetListWithPagination({
			resource: "modules",
			withPagination: false,
			sort: { field: "id", order: "DESC" },
		});
	const { data: journalsDataObj, loading: journalsLoading } =
		useGetList("journals");

	const getModuleChoices = (modules) =>
		Object.values(modules).map((module) => module);

	const getJournalChoices = (journals) => [
		{
			id: Infinity,
			name: { ar: "الكتابة الحرة" },
		},
		...Object.values(journals).map((journal) => journal),
	];

	const getHandoutChoices = (handouts) =>
		Array.isArray(handouts) ? handouts : [];

	const fetchHandouts = async () => {
		try {
			const res = await axios.get(
				envSettings.baseUrl + "/handouts/active",
				{
					withCredentials: true,
				}
			);
			setHandoutsData(getHandoutChoices(res?.data?.data || []));
			setHandoutsLoading(false);
		} catch (error) {
			console.error("Error fetching handouts:", error);
			setHandoutsLoading(false);
		}
	};

	const onFailure = (error) => {
		notify(error.message, "warning");
	};

	const { data: languagesData } = useGetList("languages");

	useEffect(() => {
		setLanguagesArray(
			Object.values(languagesData).map((language) =>
				language.name.slice(0, 2).toLowerCase()
			)
		);
	}, [languagesData]);

	useEffect(() => {
		if (!modulesLoading) setModulesData(getModuleChoices(modulesDataObj));
	}, [modulesDataObj, modulesLoading]);

	useEffect(() => {
		if (!journalsLoading)
			setJournalsData(getJournalChoices(journalsDataObj));
	}, [journalsDataObj, journalsLoading]);

	useEffect(() => {
		fetchHandouts();
	}, []);

	const defaultLocale = useMemo(
		() => userPreferences.defaultTranslationLanguage,
		[]
	);

	useEffect(() => {
		(async () => {
			const res = await axios.get(envSettings.baseUrl + "/tasks/events", {
				withCredentials: true,
			});
			setEvents(res?.data?.data);
			setLoading(false);
		})();
	}, []);

	useEffect(() => {
		if (!taskLoading && task) setCurrentEvent(task.taskEventAction);
	}, [taskLoading, task]);


	const transform = (data) => {
		const transformedData = { ...data };
		
		if (data.taskEventAction === taskEvents.listenToSingle) {
			transformedData.journalId = null;
			transformedData.handoutId = null;
		} else if (data.taskEventAction === taskEvents.createJournal) {
			transformedData.singleId = null;
			transformedData.handoutId = null;
		} else if (data.taskEventAction === taskEvents.fillHandout) {
			transformedData.singleId = null;
			transformedData.journalId = null;
		}
		
		return transformedData;
	};

	return (
		<>
			{loading || taskLoading || modulesLoading || handoutsLoading ? (
				<center>
					<h1>Loading...</h1>
				</center>
			) : (
				<Edit
					{...props}
					title={translate("tasks.edit.title")}
					mutationMode="pessimistic"
					transform={transform}
				>
					<SimpleForm
						onSuccess={onSuccess}
						onFailure={onFailure}
						toolbar={<CustomToolbar />}
						validate={validateTaskData}
						syncWithLocation={false}
					>
						<TranslatableInputs
							locales={languagesArray}
							defaultLocale={defaultLocale}
						>
							<TextInput source="name" />
						</TranslatableInputs>
						<SelectInput
							source="taskEventAction"
							choices={events}
							label={"Task Event"}
							optionText={"name"}
							optionValue={"action"}
							onChange={(event) => {
								setCurrentEvent(event.target.value);
							}}
						/>
						{currentEvent === taskEvents.listenToSingle && (
							<AutocompleteInput
								source="singleId"
								label="Module"
								choices={modulesData}
								defaultValue={task?.taskEventAction === taskEvents.listenToSingle ? task?.recordId : null}
							/>
						)}
						{currentEvent === taskEvents.createJournal && (
							<AutocompleteInput
								source="journalId"
								choices={journalsData}
								optionText={"name['ar']"}
								defaultValue={task?.taskEventAction === taskEvents.createJournal ? (task?.recordId ?? Infinity) : null}
							/>
						)}

						{currentEvent === taskEvents.fillHandout && (
							<AutocompleteInput
								source="handoutId"
								label="Handout"
								choices={handoutsData}
								optionText="title"
								defaultValue={task?.taskEventAction === taskEvents.fillHandout ? task?.recordId : null}
							/>
						)}
					</SimpleForm>
				</Edit>
			)}
		</>
	);
};
